/**
 * THESIS: Login is an access checkpoint plotted into Groundline, refusing the generic centered auth card.
 * OWN-WORLD: Plot Paper form, midnight access ledger, routing-blue controls, and one orange submit fix.
 * STORY: Identify with credentials, understand the demo boundary, then return to the requested workspace.
 * FIRST VIEWPORT: Login form leads on the left; a dark credential ledger proves the available roles on the right.
 * FORM: Access ledger, the strongest direct structure for this precise extension; inherited Groundline staging, no seed.
 */
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowRight,
  BadgeCheck,
  Eye,
  EyeOff,
  LockKeyhole,
  RouteIcon,
} from "lucide-react"
import type { FormEvent } from "react"
import { useRef, useState } from "react"

import { DemoNotice } from "@/components/demo-notice"
import { PublicPagePending } from "@/components/loading-skeleton"
import { PublicHeader } from "@/components/public-header"
import { Button } from "@/components/ui/button"
import { postLoginPath, safeInternalPath } from "@/lib/navigation"
import { demoPageQueryOptions } from "@/lib/query-options"
import { signInFn } from "@/server/functions"

type LoginSearch = { next?: string }

const DEMO_PASSWORD = "Groundline2026!"

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    next: safeInternalPath(search.next),
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(demoPageQueryOptions()),
  head: () => ({
    meta: [{ title: "Log in · Groundline" }],
  }),
  pendingComponent: PublicPagePending,
  component: LoginPage,
})

function LoginPage() {
  const { data } = useSuspenseQuery(demoPageQueryOptions())
  const { next } = Route.useSearch()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [loadedAccountId, setLoadedAccountId] = useState<string | null>(null)
  const formRef = useRef<HTMLFormElement>(null)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitting(true)
    setError("")

    try {
      const viewer = await signInFn({ data: { email, password } })
      window.location.assign(postLoginPath(next, viewer.id, viewer.role))
    } catch {
      setError(
        "Email or password did not match a demo account. Check the credentials and try again."
      )
      setSubmitting(false)
    }
  }

  return (
    <>
      <PublicHeader viewer={data.viewer} />
      <DemoNotice />
      <main className="login-page">
        <div className="site-frame">
          <div className="login-shell">
            <section className="login-form-panel">
              <span className="eyebrow">
                <LockKeyhole className="size-4" />
                Account access
              </span>
              <h1 className="login-title">Log in to Groundline.</h1>
              <p className="login-lede">
                Continue to your bookings or the course workspace assigned to
                your account.
              </p>

              <form ref={formRef} className="login-form" onSubmit={submit}>
                <div className="field">
                  <label htmlFor="login-email">Email address</label>
                  <input
                    id="login-email"
                    className="control"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    required
                    value={email}
                    placeholder="you@example.com"
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>

                <div className="field">
                  <label htmlFor="login-password">Password</label>
                  <div className="login-password">
                    <input
                      id="login-password"
                      className="control"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      minLength={8}
                      value={password}
                      placeholder="Enter your password"
                      onChange={(event) => setPassword(event.target.value)}
                    />
                    <button
                      type="button"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      aria-pressed={showPassword}
                      onClick={() => setShowPassword((value) => !value)}
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </button>
                  </div>
                </div>

                {error ? (
                  <p className="action-error" role="alert">
                    {error}
                  </p>
                ) : null}

                <Button
                  type="submit"
                  variant="accent"
                  size="lg"
                  className="w-full"
                  disabled={submitting}
                >
                  {submitting ? "Logging in…" : "Log in"}
                  <ArrowRight data-icon="inline-end" />
                </Button>
              </form>

              <div className="login-demo-link">
                <span>Prefer one-click access?</span>
                <Link to="/demo" search={{ next }}>
                  Choose a demo role
                  <ArrowRight />
                </Link>
              </div>
            </section>

            <aside className="login-ledger" aria-label="Demo login credentials">
              <svg
                className="login-ledger__route"
                viewBox="0 0 420 180"
                aria-hidden="true"
              >
                <path d="M18 134 C74 134 82 38 148 54 S217 160 280 118 S336 38 404 48" />
                <circle cx="18" cy="134" r="6" />
                <circle cx="148" cy="54" r="6" />
                <circle cx="280" cy="118" r="6" />
                <rect x="397" y="41" width="14" height="14" rx="2" />
              </svg>

              <div className="login-ledger__head">
                <span className="plot-label">Portfolio access ledger</span>
                <BadgeCheck aria-hidden="true" />
              </div>

              <div className="login-ledger__copy">
                <h2>Demo credentials</h2>
                <p>
                  Use any listed email with the shared password. These
                  identities and their records are synthetic.
                </p>
              </div>

              <div className="login-ledger__accounts">
                {data.accounts.map((account) => (
                  <button
                    key={account.id}
                    type="button"
                    aria-pressed={loadedAccountId === account.id}
                    onClick={() => {
                      setEmail(account.email)
                      setPassword(DEMO_PASSWORD)
                      setError("")
                      setLoadedAccountId(account.id)

                      if (window.matchMedia("(max-width: 760px)").matches) {
                        window.requestAnimationFrame(() => {
                          formRef.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          })
                          formRef.current
                            ?.querySelector<HTMLInputElement>("#login-email")
                            ?.focus({ preventScroll: true })
                        })
                      }
                    }}
                  >
                    <span>
                      <small>{account.role.replace("_", " ")}</small>
                      <strong>{account.email}</strong>
                    </span>
                    <span>Use</span>
                  </button>
                ))}
              </div>
              <p className="sr-only" role="status" aria-live="polite">
                {loadedAccountId
                  ? `Credentials loaded for ${
                      data.accounts.find(
                        (account) => account.id === loadedAccountId
                      )?.email ?? "the selected account"
                    }. Review the form and log in.`
                  : ""}
              </p>

              <div className="login-ledger__password">
                <span>
                  <RouteIcon aria-hidden="true" />
                  Shared password
                </span>
                <code>{DEMO_PASSWORD}</code>
              </div>

              <p className="login-ledger__note">
                Sessions last seven days on this browser. This portfolio build
                does not create production user accounts.
              </p>
            </aside>
          </div>
        </div>
      </main>
    </>
  )
}
